import { useCallback, useEffect, useState } from "react";

/**
 * An error with NO MESSAGE, carrying only a name a developer can read in a stack.
 *
 * `extractApiError` prefers `e.message` over the caller's fallback, so any wording put on
 * an error the FRONT END invents becomes the sentence a practitioner reads — an English
 * developer diagnostic, in every locale, which is exactly what the language law forbids
 * (developer-facing text is English; user-facing text goes through the locale layer).
 * Leaving the message empty is what makes the screen's own localized `errors.loadFailed`
 * the thing on screen. An error the SERVER sent keeps its own `detail`, untouched.
 */
const opaqueError = (name) => Object.assign(new Error(), { name });

/**
 * The failure a loader throws when a 200 does not carry the SHAPE the screen needs — a
 * body that is not the list it must be, or not the settings object it must be.
 *
 * A malformed 200 is a real shape to survive, not paranoia: the production proxy answers
 * 200 + index.html for an unknown `/api` path (docs/qa-and-audits — "prod proxy masks API
 * 404s"), and reading that as "this practice has nothing" is the same lie as swallowing a
 * 500. The public testimonials section already guards its payload the same way
 * (components/site/Testimonials.jsx).
 */
export const payloadShapeError = () => opaqueError("PayloadShapeError");

/**
 * Runs an admin screen's initial READ and holds its failure as a first-class state.
 *
 * WHY (BUG-051). The shape this replaces was everywhere:
 *
 *     const load = async () => { const { data } = await api.get("/admin/x"); setRows(data); };
 *     useEffect(() => { load(); }, []);
 *
 * No `catch`. Any failure — a 500, the 403 a suspended practice answers with, an expired
 * session, a dropped connection — rejects the promise, `setRows` never runs, the state
 * stays at its initial value, and the screen renders that value as if it were the answer:
 * "No products yet", "No messages yet", a blank copy field, a slot badged "Default". The
 * practitioner is told something FALSE about their own practice, and the natural next
 * move (re-type it, save it) destroys real data.
 *
 * A read has three outcomes — loaded-with-rows, loaded-and-genuinely-empty, and
 * could-not-load — and the third was indistinguishable from the second. This hook makes
 * it distinguishable, once, instead of five hand-rolled copies that drift.
 *
 * `load` MUST BE STABLE (`useCallback`), because it is this hook's effect dependency: a
 * loader redefined on every render would refetch forever. That is deliberate rather than
 * papered over with a ref — the screens keep calling the very same `load` after a write
 * to refresh their list, and a stable identity is what lets both callers share it.
 *
 * The raw error is returned, not a message: turning it into prose needs `t`, which a hook
 * outside a component tree has no business binding. The caller does
 * `extractApiError(loadError, tv("errors.loadFailed"), tv)` at render time.
 *
 * `loadError` is cleared on every ATTEMPT that succeeds, so a recovered read stops
 * reporting a failure that is over; `loading` is true during the first read and during
 * every retry.
 *
 * @param {() => Promise<any>} load stable async reader; may throw.
 * @returns {{loading: boolean, loadError: any, reload: () => Promise<void>}}
 */
export default function useAdminLoad(load) {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      await load();
      setLoadError(null);
    } catch (e) {
      // Never null/undefined: `loadError` is what the screen tests for truthiness, and an
      // error thrown as `undefined` (a rejected promise with no reason) would otherwise
      // read as success.
      setLoadError(e || opaqueError("ReadFailed"));
    } finally {
      setLoading(false);
    }
  }, [load]);

  useEffect(() => { reload(); }, [reload]);

  return { loading, loadError, reload };
}
